import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Spin, Switch } from "antd";
import { toast } from "react-toastify";
import { UserContext } from "../App";
import { isAdminRole } from "../constants/general.constant";
import {
  DEFAULT_SPEED_INFRINGEMENT_RULES,
  normalizeSpeedInfringementRules,
} from "../constants/speedInfringementRules.constant";
import {
  saveSpeedInfringementRules,
  subscribeSpeedInfringementRules,
} from "../services/firestoreTracking.service";

export const SpeedInfringementSettingsPanel = () => {
  const { user } = useContext(UserContext);
  const canEdit = isAdminRole(user);
  const [rules, setRules] = useState(DEFAULT_SPEED_INFRINGEMENT_RULES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    return subscribeSpeedInfringementRules(
      (nextRules) => {
        setRules(nextRules);
        setLoading(false);
        setError(null);
      },
      (nextError) => {
        console.error("[SPEED RULES] subscription failed", nextError);
        setError(nextError);
        setRules(normalizeSpeedInfringementRules());
        setLoading(false);
      }
    );
  }, []);

  const activeCount = useMemo(
    () => rules.filter((rule) => rule.enabled).length,
    [rules]
  );

  const updateRule = async (ruleId, enabled) => {
    const nextRules = rules.map((rule) =>
      rule.id === ruleId ? { ...rule, enabled } : rule
    );
    await persistRules(nextRules, rules);
  };

  const persistRules = async (nextRules, rollbackRules = rules) => {
    setRules(nextRules);
    setSaving(true);
    try {
      await saveSpeedInfringementRules(nextRules, user);
      toast.success("Speed infringement settings updated");
    } catch (nextError) {
      console.error("[SPEED RULES] save failed", nextError);
      setRules(rollbackRules);
      toast.error("Failed to update speed infringement settings");
    } finally {
      setSaving(false);
    }
  };

  return <div className="rounded-[28px] border border-[#ece5dd] bg-white px-6 py-6 shadow-[0_18px_55px_rgba(15,23,42,0.05)]">
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <span className="text-[1.15rem] font-semibold text-[#161311]">Speed infringement switches</span>
          <p className="mb-0 mt-1 text-sm text-[#6e6258]">
            Enabled rows create speeding infringements, Telegram alerts, and admin/regional CMS records.
          </p>
        </div>
        <div className="text-sm font-semibold text-[#6e6258]">
          {loading ? "Loading rules" : `${activeCount} of ${rules.length} active`}
        </div>
      </div>
      {!canEdit ? <Alert
        type="info"
        showIcon
        message="Regional managers can view these rules. Only admin users can change them."
      /> : null}
      {error ? <Alert
        type="warning"
        showIcon
        message="Could not load saved rules. Default rules are shown."
      /> : null}
    </div>

    {loading ? <div className="flex justify-center py-8"><Spin /></div> : <div className="mt-5 grid grid-cols-1 gap-3 xl:grid-cols-5">
      {rules.map((rule) => {
        const triggerSpeed = Number(rule.speedLimitKph) + Number(rule.toleranceKph);
        return <div
          key={rule.id}
          className={`rounded-2xl border px-4 py-4 ${rule.enabled ? "border-[#f2c49b] bg-[#fffaf4]" : "border-[#ece5dd] bg-[#f8f5f1]"}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-[#161311]">
                {rule.speedLimitKph} km/h +{rule.toleranceKph}
              </div>
              <div className="mt-1 text-xs text-[#6e6258]">
                Triggers from {triggerSpeed} km/h
              </div>
            </div>
            <Switch
              checked={rule.enabled}
              disabled={!canEdit || saving}
              onChange={(checked) => updateRule(rule.id, checked)}
            />
          </div>
        </div>;
      })}
    </div>}

    {canEdit ? <div className="mt-4 flex justify-end">
      <Button
        disabled={saving}
        onClick={() => persistRules(DEFAULT_SPEED_INFRINGEMENT_RULES, rules)}
      >
        Restore defaults
      </Button>
    </div> : null}
  </div>;
};
